// Image actions (ui-spec §6.11): Delete confirms with the scoped command. Catalog images belong to the platform, so
// they offer none; a project Image offers Copy digest once it has built.
import { objectPath, useApi } from "@nodus/api";
import { cliCommand, ConfirmDestructive, toast, usePrefs, type MenuAction } from "@nodus/ui";
import * as React from "react";
import { CATALOG_PROJECT, type Image, type Scope } from "./model";
import { useRun } from "./parts";

export function useImageActions(scope: Scope, opts: { onDeleted?: () => void } = {}) {
  const { client } = useApi();
  const prefs = usePrefs();
  const { busy, run } = useRun();
  const [pending, setPending] = React.useState<Image>();

  const menu = (img: Image): MenuAction[] => [
    ...(img.status?.digest
      ? [{ label: "Copy digest", onSelect: () => void navigator.clipboard?.writeText(img.status?.digest ?? "").then(() => toast("Digest copied")).catch(() => undefined) }]
      : []),
    ...(img.metadata.namespace === CATALOG_PROJECT ? [] : [{ label: "Delete", destructive: true, separatorBefore: true, onSelect: () => setPending(img) }]),
  ];

  const name = pending?.metadata.name ?? "";
  const dialog = pending ? (
    <ConfirmDestructive
      open
      onOpenChange={(o) => !o && setPending(undefined)}
      pending={busy}
      title={`Delete image ${name}?`}
      consequences={<p>The image and its tags are removed from the project. Anything that names it cannot start until an Image with that name exists again.</p>}
      cli={cliCommand(["delete", `image/${name}`], scope, { omitScope: prefs.omitScope })}
      confirmText={name}
      actionLabel="Delete image"
      onConfirm={() =>
        void run(() => client.delete(objectPath("Image", name, scope.project), { org: scope.org }), `Deleted ${name}`).then((ok) => {
          if (ok) {
            setPending(undefined);
            opts.onDeleted?.();
          }
        })
      }
    />
  ) : null;
  return { menu, dialog, busy };
}
